/**
 * Firestore persistence for voice sessions (F2.4).
 *
 * The voice pipeline runs entirely on the client, so the client writes the
 * transcribed turns itself (the rules allow the owner with the `allowed` claim
 * to create/update conversations and messages). Shapes mirror what the backend
 * writes (`functions/src/data/conversations.ts`) and what `useConversations` /
 * `useMessages` read: docs carry their own `id`, timestamps are epoch millis,
 * and NO field is ever written as `undefined` (Firestore rejects it).
 */
import type { MessageDoc, MessagePart } from '@agente/shared'
import { doc, setDoc } from 'firebase/firestore'

import { conversationDoc, conversationsCol, messagesCol } from './firestore'
import { formatDateTime } from './format'

/** One completed voice turn: what the user said, what the agent answered. */
export interface VoiceTurn {
  userText: string
  assistantText: string
  /** Tool activity of the turn, rendered by the existing ToolChip. */
  toolParts: MessagePart[]
}

const PREVIEW_MAX_CHARS = 120

/** Creates the conversation that will hold the voice session; returns its id. */
export async function createVoiceConversation(uid: string): Promise<string> {
  const ref = doc(conversationsCol(uid))
  const now = Date.now()
  await setDoc(ref, {
    id: ref.id,
    title: `Conversa por voz — ${formatDateTime(now)}`,
    createdAt: now,
    updatedAt: now,
  })
  return ref.id
}

/** Bumps `updatedAt`/`lastPreview` on the conversation (merge, backend-compatible). */
function touchConversation(uid: string, conversationId: string, preview: string): Promise<void> {
  return setDoc(
    conversationDoc(uid, conversationId),
    { updatedAt: Date.now(), lastPreview: preview.slice(0, PREVIEW_MAX_CHARS) },
    { merge: true },
  )
}

/**
 * Persists one completed turn: a `user` message with the input transcription
 * and an `assistant` message with the output transcription + tool parts. Either
 * side may be absent (e.g. transcription fragments missing); nothing is written
 * for a fully empty turn.
 */
export async function persistVoiceTurn(
  uid: string,
  conversationId: string,
  turn: VoiceTurn,
): Promise<void> {
  const userText = turn.userText.trim()
  const assistantText = turn.assistantText.trim()
  const now = Date.now()
  const writes: Promise<void>[] = []

  if (userText.length > 0) {
    const ref = doc(messagesCol(uid, conversationId))
    const message: MessageDoc = {
      id: ref.id,
      role: 'user',
      content: userText,
      status: 'complete',
      channel: 'voice',
      createdAt: now,
      updatedAt: now,
    }
    writes.push(setDoc(ref, message))
  }

  if (assistantText.length > 0 || turn.toolParts.length > 0) {
    const ref = doc(messagesCol(uid, conversationId))
    // +1ms so the assistant message sorts after the user message of the turn.
    const message: MessageDoc = {
      id: ref.id,
      role: 'assistant',
      content: assistantText,
      status: 'complete',
      channel: 'voice',
      createdAt: now + 1,
      updatedAt: now + 1,
    }
    if (turn.toolParts.length > 0) message.parts = turn.toolParts
    writes.push(setDoc(ref, message))
  }

  if (writes.length === 0) return

  const preview = assistantText.length > 0 ? assistantText : userText
  if (preview.length > 0) writes.push(touchConversation(uid, conversationId, preview))
  await Promise.all(writes)
}

/**
 * Fallback marker when the session produced no persistable transcription
 * (e.g. transcription unavailable on the backend): records that a voice
 * conversation took place so the thread is not silently empty.
 */
export async function persistVoiceMarker(uid: string, conversationId: string): Promise<void> {
  const ref = doc(messagesCol(uid, conversationId))
  const now = Date.now()
  const content = 'Conversa por voz realizada.'
  const message: MessageDoc = {
    id: ref.id,
    role: 'assistant',
    content,
    status: 'complete',
    channel: 'voice',
    createdAt: now,
    updatedAt: now,
  }
  await Promise.all([setDoc(ref, message), touchConversation(uid, conversationId, content)])
}
